import React from 'react';
import { formatMoney } from '../../utils/format';
import { AlertCircle, Users } from 'lucide-react';
import { useTranslation } from '../../i18n';

/**
 * AnnualHRReport — RH block of the annual board meeting (climat social,
 * démissions évitées, profils en difficulté, coût RH évitable). Extracted
 * from `AnnualBoardMeeting.jsx` (04/06/2026 refactor). Pure presentation.
 */
export default function AnnualHRReport({ rhReport, avoidableHrCost }) {
  const { t } = useTranslation();
  if (!rhReport) return null;
  return (
    <div className="border-t border-ink-600 pt-4" data-testid="rh-annual-report">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded bg-rose-900/30">
          <Users className="w-4 h-4 text-rose-400" />
        </div>
        <div>
          <h4 className="font-bold text-sm text-slate-200">Bilan RH de l&apos;exercice</h4>
          <p className="text-[10px] text-slate-500">Climat social, démissions évitées, profils sous-utilisés</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className={`rounded-lg p-2.5 text-center border ${
          rhReport.climatSocial >= 75 ? 'bg-money/5 border-money/40' :
          rhReport.climatSocial >= 50 ? 'bg-warn/5 border-warn/40' :
          'bg-danger/5 border-danger/40'
        }`} data-testid="rh-climat-social">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Climat social</p>
          <p className={`text-2xl font-black font-mono ${
            rhReport.climatSocial >= 75 ? 'text-money' :
            rhReport.climatSocial >= 50 ? 'text-warn' :
            'text-danger'
          }`}>{rhReport.climatSocial}<span className="text-xs text-slate-500">/100</span></p>
          <p className="text-[9px] text-slate-500">Sat. moy. {rhReport.avgSatisfaction} · Tournov. {rhReport.turnoverRate}%</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2.5 text-center border border-ink-600" data-testid="rh-burnout-saves">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Démissions évitées</p>
          <p className="text-2xl font-black font-mono text-money">{rhReport.retentionSaves ?? rhReport.burnoutAlertsResolved}</p>
          <p className="text-[9px] text-slate-500">
            {rhReport.retentionSavesDetail
              ? [
                rhReport.retentionSavesDetail.burnout > 0 ? `${rhReport.retentionSavesDetail.burnout} burnout` : null,
                (rhReport.retentionSavesDetail.renego + rhReport.retentionSavesDetail.align) > 0
                  ? `${rhReport.retentionSavesDetail.renego + rhReport.retentionSavesDetail.align} renégo. salariale${(rhReport.retentionSavesDetail.renego + rhReport.retentionSavesDetail.align) > 1 ? 's' : ''}` : null,
                rhReport.retentionSavesDetail.poach > 0 ? `${rhReport.retentionSavesDetail.poach} contre-débauchage` : null,
                rhReport.retentionSavesDetail.pressure > 0 ? `${rhReport.retentionSavesDetail.pressure} congés accordés` : null,
              ].filter(Boolean).join(' · ') || 'Aucune rétention active cette année'
              : 'Alertes burnout traitées'}
          </p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2.5 text-center border border-ink-600">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">CA de l'exercice</p>
          <p className="text-base font-bold font-mono text-slate-200">{formatMoney(rhReport.yearRevenue)}</p>
          <p className={`text-[9px] font-mono ${rhReport.yearProfit >= 0 ? 'text-money' : 'text-danger'}`}>
            Résultat {rhReport.yearProfit >= 0 ? '+' : ''}{formatMoney(rhReport.yearProfit)}
          </p>
          <p className="text-[8px] text-slate-600">Chiffre d'affaires facturé sur l'année écoulée</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2.5 text-center border border-ink-600">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Effectifs</p>
          <p className="text-base font-bold text-slate-200">{rhReport.teamSize}</p>
          <p className="text-[9px] text-slate-500">{rhReport.fleetSize} véhicules</p>
        </div>
      </div>

      {rhReport.worstAssignments && rhReport.worstAssignments.length > 0 && (
        <div className="bg-danger/5 border border-danger/30 rounded-lg p-3" data-testid="rh-worst-assignments">
          <p className="text-[10px] uppercase tracking-wider text-danger font-mono mb-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Profils en difficulté (sat. &lt; 40)
          </p>
          <ul className="space-y-1">
            {rhReport.worstAssignments.map((d) => (
              <li key={d.id} className="flex items-center justify-between text-xs">
                <span className="font-bold text-slate-200">{d.name}</span>
                <span className="text-[10px] text-danger font-mono">Sat. {Math.round(d.satisfaction)}/100</span>
              </li>
            ))}
          </ul>
          <p className="text-[9px] text-slate-500 italic mt-2">Réassigne, accorde une prime ou propose une formation depuis /hr.</p>
        </div>
      )}

      {/* 02/06/2026 — Coût RH évitable : pédagogie sur la valeur de la fidélisation. */}
      {avoidableHrCost && avoidableHrCost.count > 0 && (
        <div
          className="mt-3 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3"
          data-testid="rh-avoidable-cost"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-amber-300 font-mono flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {t('rh_avoidable_cost_title')}
            </p>
            <span className="text-base font-mono font-black text-amber-200" data-testid="rh-avoidable-total">
              {formatMoney(avoidableHrCost.total)}
            </span>
          </div>
          <p className="text-[11px] text-slate-300 leading-relaxed">
            {t(
              avoidableHrCost.count > 1 ? 'rh_avoidable_cost_summary_many' : 'rh_avoidable_cost_summary_one',
              { n: avoidableHrCost.count },
            )}{' '}
            {avoidableHrCost.byMode.rupture > 0 && (
              <span>{t('rh_avoidable_cost_breakdown_rupture', { amount: formatMoney(avoidableHrCost.byMode.rupture) })}</span>
            )}
            {avoidableHrCost.byMode.fault > 0 && (
              <span>{t('rh_avoidable_cost_breakdown_fault', { amount: formatMoney(avoidableHrCost.byMode.fault) })}</span>
            )}
            {avoidableHrCost.byMode.probation > 0 && (
              <span>{t('rh_avoidable_cost_breakdown_probation', { amount: formatMoney(avoidableHrCost.byMode.probation) })}</span>
            )}
          </p>
          <p className="text-[9px] text-slate-500 italic mt-1.5">
            {t('rh_avoidable_cost_tip')}
          </p>
        </div>
      )}
    </div>
  );
}
